import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import API from '../api';
import { getImageUrl, handleImgError } from '../utils/imageUtils';

const SearchBar = ({ onClose, isMobile = false }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      const { data } = await API.get(`/api/products/suggestions?q=${encodeURIComponent(searchQuery.trim())}`);
      setSuggestions(data.suggestions || []);
      setShowDropdown(true);
      setActiveIndex(-1);
    } catch (error) {
      console.error('Search suggestions error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Debounce 300ms
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (query.trim()) {
      navigate(`/products?search=${encodeURIComponent(query.trim())}`);
      setQuery('');
      setShowDropdown(false);
      setSuggestions([]);
      if (onClose) onClose();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    navigate(`/products/${suggestion._id}`);
    setQuery('');
    setShowDropdown(false);
    setSuggestions([]);
    if (onClose) onClose();
  };

  const handleKeyDown = (e) => {
    const totalItems = suggestions.length + (query.trim() ? 1 : 0); // +1 for "see all results"

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        handleSuggestionClick(suggestions[activeIndex]);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      if (onClose) onClose();
    }
  };

  // Image URL resolution handled by centralized getImageUrl utility

  return (
    <div className={`relative ${isMobile ? 'w-full' : ''}`}>
      <form onSubmit={handleSearch} className="flex items-center">
        <div className={`relative ${isMobile ? 'w-full' : 'w-48 md:w-72'}`}>
          <FiSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={16}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => query.trim().length >= 2 && suggestions.length > 0 && setShowDropdown(true)}
            placeholder="Search bags, wallets..."
            className={`w-full pl-9 pr-8 py-2 text-sm border border-gold/40 rounded-full
                       focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold
                       transition-all bg-white ${isMobile ? 'py-2.5' : ''}`}
            autoComplete="off"
            id="search-bar-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setSuggestions([]);
                setShowDropdown(false);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400
                         hover:text-charcoal transition-colors"
            >
              <FiX size={14} />
            </button>
          )}
        </div>
        {!isMobile && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-2 text-gray-400 hover:text-charcoal transition-colors"
          >
            <FiX size={18} />
          </button>
        )}
      </form>

      {/* Suggestions Dropdown */}
      {showDropdown && (query.trim().length >= 2) && (
        <div
          ref={dropdownRef}
          className={`absolute top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100
                     overflow-hidden z-[100] animate-fadeInUp ${isMobile ? 'left-0 right-0' : 'left-0 w-80 md:w-96'}`}
          style={{ animationDuration: '0.2s' }}
          id="search-suggestions-dropdown"
        >
          {loading ? (
            <div className="p-4 text-center">
              <div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full
                            animate-spin mx-auto" />
            </div>
          ) : suggestions.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-400">
              No products found for "{query}"
            </div>
          ) : (
            <>
              {suggestions.map((item, index) => (
                <button
                  key={item._id}
                  onClick={() => handleSuggestionClick(item)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
                             border-b border-gray-50 last:border-0
                             ${activeIndex === index ? 'bg-gold/5' : 'hover:bg-gray-50'}`}
                  id={`suggestion-${index}`}
                >
                  <img
                    src={getImageUrl(item.thumbnail)}
                    alt={item.name}
                    className="w-12 h-12 rounded-lg object-cover bg-gray-100 flex-shrink-0"
                    onError={handleImgError}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-charcoal truncate">{item.name}</p>
                    <p className="text-sm text-gold font-semibold">
                      ₹{item.price?.toLocaleString('en-IN')}
                    </p>
                  </div>
                </button>
              ))}
            </>
          )}

          {/* "See all results" footer */}
          {query.trim() && (
            <button
              onClick={handleSearch}
              className={`w-full px-4 py-3 text-sm text-center font-medium transition-colors
                         border-t border-gray-100
                         ${activeIndex === suggestions.length ? 'bg-gold/5 text-gold' : 'text-gray-500 hover:bg-gray-50 hover:text-gold'}`}
              id="search-see-all"
            >
              Press Enter to see all results for "<span className="font-semibold">{query}</span>"
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
